import React from "react";

/**
 * Picture - responsive real-photo <picture> element that consumes a manifest entry.
 * Uses WebP with a JPEG fallback and srcset for responsive delivery.
 *
 * CLS defense: sets aspect-ratio on the <picture> from the intrinsic dimensions
 * so space is reserved even when the parent container has no explicit aspect
 * ratio. Callers that DO wrap us in an aspect-[4/3]-style parent still work
 * — object-fit: cover on the img keeps the crop clean.
 */
export function Picture({
  photo,
  className = "",
  loading = "lazy",
  sizes = "(min-width: 1280px) 50vw, (min-width: 768px) 66vw, 100vw",
  imgClassName = "",
  fetchPriority,
  onClick,
  testId,
  aspectRatio, // optional override, e.g. "16 / 10"
}) {
  if (!photo) return null;
  const widths = Object.keys(photo.webp || {}).map(Number).sort((a, b) => a - b);
  const webpSrcSet = widths.map((w) => `${photo.webp[w]} ${w}w`).join(", ");
  const fallback = photo.jpg || photo.webp[widths[widths.length - 1]];
  const intrinsicRatio =
    aspectRatio ||
    (photo.width && photo.height ? `${photo.width} / ${photo.height}` : undefined);
  // Force block-level and reserve space via aspect-ratio.
  const pictureStyle = intrinsicRatio
    ? { display: "block", aspectRatio: intrinsicRatio }
    : { display: "block" };
  return (
    <picture className={className} onClick={onClick} style={pictureStyle}>
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      <img
        src={fallback}
        alt={photo.alt || ""}
        loading={loading}
        decoding="async"
        fetchPriority={fetchPriority}
        className={imgClassName}
        width={photo.width}
        height={photo.height}
        data-testid={testId}
      />
    </picture>
  );
}

/** Find a photo entry by slug, tolerant to prefix matches. */
export function findPhoto(photos, slugOrPrefix) {
  if (!photos?.length) return null;
  const exact = photos.find((p) => p.slug === slugOrPrefix);
  if (exact) return exact;
  return photos.find((p) => p.slug.startsWith(slugOrPrefix));
}

/** Return N photos of a given category (or any if not found). */
export function photosByCategory(photos, category, n = 6) {
  if (!photos?.length) return [];
  const of = photos.filter((p) => p.category === category);
  return of.slice(0, n);
}
